const SNOOZE_KEY = 'pwa-install-snoozed-until'
const SNOOZE_MS = 14 * 24 * 60 * 60 * 1000

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

// État PWA partagé : invite d'installation (Android/Chrome), consigne iOS, mode hors ligne.
export function usePwa() {
  const deferredPrompt = useState<BeforeInstallPromptEvent | null>('pwa-deferred-prompt', () => null)
  const isStandalone = useState('pwa-standalone', () => false)
  const isIos = useState('pwa-ios', () => false)
  const isOffline = useState('pwa-offline', () => false)
  const snoozed = useState('pwa-snoozed', () => true)

  const guideOpen = useState('pwa-guide-open', () => false)

  // Installable (Chrome/Android via l'invite native, iOS via « Sur l'écran d'accueil »)
  const installable = computed(() => !isStandalone.value && (!!deferredPrompt.value || isIos.value))
  // Bannière flottante : respecte le report de 14 jours
  const canInstall = computed(() => installable.value && !snoozed.value)

  // Invite native quand elle existe, sinon le guide pas à pas
  async function promptInstall() {
    if (deferredPrompt.value) {
      await install()
    }
    else {
      guideOpen.value = true
    }
  }

  async function install() {
    const evt = deferredPrompt.value
    if (!evt) {
      return
    }
    await evt.prompt()
    await evt.userChoice
    deferredPrompt.value = null
  }

  function dismiss() {
    snoozed.value = true
    try {
      localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_MS))
    }
    catch {
      // Stockage indisponible : l'invite réapparaîtra à la prochaine visite.
    }
  }

  function init() {
    const toast = useToast()
    const ua = navigator.userAgent
    isIos.value = /iphone|ipad|ipod/i.test(ua) || (ua.includes('Mac') && navigator.maxTouchPoints > 1)
    isStandalone.value = window.matchMedia('(display-mode: standalone)').matches || (navigator as any).standalone === true
    isOffline.value = !navigator.onLine
    try {
      snoozed.value = Number(localStorage.getItem(SNOOZE_KEY) || 0) > Date.now()
    }
    catch {
      snoozed.value = false
    }

    window.addEventListener('online', () => (isOffline.value = false))
    window.addEventListener('offline', () => (isOffline.value = true))
    window.addEventListener('beforeinstallprompt', (e) => {
      e.preventDefault()
      deferredPrompt.value = e as BeforeInstallPromptEvent
    })
    window.addEventListener('appinstalled', () => {
      deferredPrompt.value = null
      isStandalone.value = true
      guideOpen.value = false
      toast.add({ title: 'Application installée 🎉', description: 'Retrouve l\'espace volontaire sur ton écran d\'accueil.', color: 'success' })
    })
  }

  return { canInstall, installable, guideOpen, isIos, isStandalone, isOffline, install, promptInstall, dismiss, init }
}
